import logoBrandStarter from '../assets/logo_brandstarter.png';
import { useState, useRef, useEffect } from 'react';
import { api } from '../services/api';
import { CSS as BASE_CSS } from './ModulePersona';
import { CSS as FICHE_CSS } from './ModulePositionnement';

// Moteur générique : cours, quiz, conversation avec Clara, fiche. Une config par module.
function getCtx(contextePrecedent) {
  const list = Array.isArray(contextePrecedent) ? contextePrecedent : [];
  const find = (num) => list.find(c => Number(c.numeroModule) === num)?.contenu || '';
  const brand = (find(1).match(/NOM_MARQUE:\s*(.+)/)?.[1] || '').trim() || 'ta marque';
  const persona = (find(2).match(/PERSONA_PRENOM:\s*(.+)/)?.[1] || '').trim() || 'ton client idéal';
  return { brand, persona };
}

function parseKeys(text) {
  const o = {};
  (text || '').split('\n').forEach(l => {
    const m = l.match(/^\s*([A-Z_0-9]+):\s*(.*)$/);
    if (m) o[m[1]] = m[2].replace(/\*\*/g, '').replace(/^\[|\]$/g, '').trim();
  });
  return o;
}

function Fiche({ cfg, synthese, brand }) {
  const d = parseKeys(synthese);
  const ref = useRef(null);
  const download = () => {
    const el = ref.current; if (!el) return;
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    document.head.appendChild(s);
    s.onload = () => {
      window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(canvas => {
        const a = document.createElement('a');
        a.download = `${cfg.fichier}-${(brand || 'marque').toLowerCase().replace(/\s+/g, '-')}.png`;
        a.href = canvas.toDataURL('image/png'); a.click();
      });
    };
  };
  const split = (v) => (v || '').split('|').map(x => x.trim()).filter(Boolean);
  const score = parseInt(d.COHERENCE_SCORE, 10);
  const warn = split(d.COHERENCE_WARN)[0];
  return (
    <div ref={ref} className="fq">
      <div className="fq-head"><div className="fq-kick">{cfg.kicker}</div><div className="fq-brand">{brand}</div></div>
      {d[cfg.hero.key] && <div className="fq-promise"><div className="fq-kick">{cfg.hero.label}</div><p>{d[cfg.hero.key]}</p></div>}
      <div className="fq-body">
        {cfg.sections.map((sec, i) => {
          const keys = sec.keys || [sec.key];
          const vals = keys.map(k => d[k]).filter(Boolean);
          if (!vals.length) return null;
          return (
            <div className="fq-sec" key={i}>
              <div className="fq-lab">{sec.label}</div>
              {cfg.copy && sec.type !== 'cards' && <button className="fq-cp" onClick={() => { try { navigator.clipboard.writeText(vals[0]); } catch (e) {} }}>Copier</button>}
              {sec.type === 'quote' && <div className="fq-phrase">{vals[0]}</div>}
              {sec.type === 'text' && <div className="fq-txt">{vals[0]}</div>}
              {sec.type === 'list' && <ul className="fq-list">{split(vals[0]).map((x, j) => <li key={j}>{x}</li>)}</ul>}
              {sec.type === 'chips' && <div className="fq-chips">{split(vals[0]).map((x, j) => <span key={j} className="fq-chip">{x}</span>)}</div>}
              {sec.type === 'cards' && <ul className="fq-list">{vals.map((v, j) => { const p = split(v); return <li key={j}><b>{p[0]}</b>{p[1] ? ` : ${p[1]}` : ''}{p[2] ? ` (${p[2]})` : ''}</li>; })}</ul>}
            </div>
          );
        })}
        {!isNaN(score) && <div className="fq-sec"><div className="fq-score"><b>{score}</b><span>Score de cohérence sur 100.{warn ? ` À surveiller : ${warn}.` : ''}</span></div></div>}
        {d.SYNTHESE && <div className="fq-sec"><div className="fq-lab">En résumé</div><div className="fq-txt">{d.SYNTHESE}</div></div>}
      </div>
      <div className="fq-foot"><img src={logoBrandStarter} alt="BrandStarter" /><button className="fq-dl" onClick={download}>Télécharger PNG</button></div>
    </div>
  );
}

export function ModuleGuide({ cfg, moduleValide, syntheseExistante, onValidate, contextePrecedent }) {
  const ctx = getCtx(contextePrecedent);
  const [phase, setPhase] = useState(moduleValide ? 'done' : 'formation');
  const [formationStep, setFormationStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizOk, setQuizOk] = useState(false);
  const [convMessages, setConvMessages] = useState([]);
  const [convInput, setConvInput] = useState('');
  const [convLoading, setConvLoading] = useState(false);
  const [convStep, setConvStep] = useState(0);
  const [synthese, setSynthese] = useState(syntheseExistante || '');
  const [error, setError] = useState('');
  const [checks, setChecks] = useState({});
  const chatEndRef = useRef(null);
  const Q = cfg.questions;

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [convMessages, error]);
  useEffect(() => { window.scrollTo(0, 0); }, [phase]);
  const qText = (i) => Q[i].q(ctx);
  useEffect(() => {
    if (phase === 'conversation' && convMessages.length === 0) setConvMessages([{ role: 'assistant', content: `${cfg.accroche} ${qText(0)}` }]);
    // eslint-disable-next-line
  }, [phase]);

  const total = cfg.formation.length;
  const progress = phase === 'formation' ? ((formationStep + 1) / (total + 2)) * 100
    : phase === 'quiz' ? ((total + 1) / (total + 2)) * 100 : phase === 'conversation' ? ((total + 1.5) / (total + 2)) * 100 : 100;

  const submitQuiz = () => {
    if (!cfg.quiz.every((_, i) => quizAnswers[i] !== undefined)) { setError('Réponds à toutes les questions avant de continuer.'); return; }
    setQuizSubmitted(true); setQuizOk(cfg.quiz.every((q, i) => quizAnswers[i] === q.correct)); setError('');
  };

  const challengePrompt = (step, answer, next) => {
    const blocChange = Q[next].bloc !== Q[step].bloc;
    return `Tu es Clara, guide IA de BrandStarter. Tu construis avec le créateur le module "${cfg.nom}" de la marque ${ctx.brand}, en ${Q.length} questions. La cible est ${ctx.persona}.

ÉTAPE ACTUELLE : ${step + 1}/${Q.length} (bloc : ${Q[step].bloc})
QUESTION POSÉE : "${qText(step)}"
RÉPONSE DE L'UTILISATEUR : "${answer}"
${blocChange ? `\nTu passes au bloc "${Q[next].bloc}". Annonce ce changement de manière fluide, en une demi-phrase.\n` : ''}
TON RÔLE :
1. Réagis à la réponse en 1 ou 2 phrases. Si elle est vague ou générique, challenge avec bienveillance et demande un exemple concret. Si elle est précise, valide chaleureusement.
2. Pose la question suivante : "${qText(next)}"

RÈGLES STRICTES :
- Tutoiement obligatoire
- Jamais de tiret long, utilise ":" ou une virgule
- Ne redemande jamais le nom de la marque ni ce qui figure déjà dans le contexte des modules précédents
- Maximum 4 lignes au total`;
  };

  const synthPrompt = (msgs) => {
    const qa = msgs.filter(m => m.role === 'user').map((m, i) => `Q${i + 1} (${qText(i)}) : ${m.content}`).join('\n');
    return `Tu es Clara, guide IA de BrandStarter. À partir des ${Q.length} réponses du créateur, génère la fiche "${cfg.nom}" de la marque ${ctx.brand} (cible : ${ctx.persona}).

RÉPONSES COLLECTÉES :
${qa}

Génère la fiche EXACTEMENT dans ce format (une clé par ligne, valeurs concrètes tirées des réponses, jamais de généralités) :

${cfg.format(ctx)}

IMPORTANT : respecte exactement ce format, tutoiement dans la synthèse, aucun tiret long, aucun markdown.`;
  };

  const send = async () => {
    if (!convInput.trim() || convLoading) return;
    const userInput = convInput.trim();
    const newMsgs = [...convMessages, { role: 'user', content: userInput }];
    setConvMessages(newMsgs); setConvInput(''); setConvLoading(true); setError('');
    try {
      const next = convStep + 1;
      if (next < Q.length) {
        const res = await api.chatIA(cfg.num, { conversation: [{ role: 'user', content: challengePrompt(convStep, userInput, next) }], tourActuel: convStep, contextePrecedent });
        setConvMessages(prev => [...prev, { role: 'assistant', content: res.message }]);
        setConvStep(next);
      } else {
        const res = await api.chatIA(cfg.num, { conversation: [{ role: 'user', content: synthPrompt(newMsgs) }], tourActuel: 10, contextePrecedent });
        setConvMessages(prev => [...prev, { role: 'assistant', content: "Parfait, j'ai tout ce qu'il me faut. Voici ta fiche..." }]);
        setSynthese(res.message.replace(/^\s*(\*\*)?VALID[EÉ]\s*:\*?\*?\s*/i, '').trim());
        setPhase('done');
      }
    } catch (e) { setError(e.message); } finally { setConvLoading(false); }
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const bloc = Q[Math.min(convStep, Q.length - 1)].bloc;

  return (
    <div className="mp">
      <style>{BASE_CSS}</style><style>{FICHE_CSS}</style>
      <div className="mp-progress"><div className="mp-progress-fill" style={{ width: `${progress}%` }}></div></div>

      {phase === 'formation' && (
        <div className="mp-formation">
          {cfg.formation.map((part, idx) => idx <= formationStep && (
            <div key={idx} className="mp-part"><div className="mp-part-num">{part.num}</div><h2>{part.titre}</h2>{part.content()}</div>
          ))}
          <div className="mp-nav-btns">
            {formationStep > 0 && <button className="mp-btn-secondary" onClick={() => setFormationStep(s => s - 1)}>← Précédent</button>}
            <div style={{ flex: 1 }} />
            {formationStep < total - 1
              ? <button className="mp-btn-primary" onClick={() => setFormationStep(s => s + 1)}>Partie suivante</button>
              : <button className="mp-btn-primary" onClick={() => setPhase('quiz')}>Valider mes connaissances</button>}
          </div>
        </div>
      )}

      {phase === 'quiz' && (
        <div className="mp-quiz">
          <div className="mp-part-num" style={{ marginBottom: 20 }}>Quiz de validation</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(20px,2.5vw,26px)', color: '#173B34', marginBottom: 24 }}>Vérifions que les bases sont bien là.</h2>
          {cfg.quiz.map((q, qi) => (
            <div key={qi} className="mp-quiz-q">
              <div className="mp-quiz-q-text">{qi + 1}. {q.q}</div>
              <div className="mp-quiz-options">
                {q.opts.map((opt, oi) => {
                  let c = 'mp-quiz-opt';
                  if (quizSubmitted) { if (oi === q.correct) c += ' correct'; else if (quizAnswers[qi] === oi) c += ' wrong'; }
                  else if (quizAnswers[qi] === oi) c += ' selected';
                  return <div key={oi} className={c} onClick={() => !quizSubmitted && setQuizAnswers(a => ({ ...a, [qi]: oi }))}>{opt}</div>;
                })}
              </div>
              {quizSubmitted && <div className={`mp-quiz-feedback ${quizAnswers[qi] === q.correct ? 'ok' : 'ko'}`}>{quizAnswers[qi] === q.correct ? '✅ ' : '❌ '}{q.feedback}</div>}
            </div>
          ))}
          {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <div className="mp-nav-btns">
            <button className="mp-btn-secondary" onClick={() => setPhase('formation')}>← Revoir le cours</button>
            {!quizSubmitted ? <button className="mp-btn-primary" onClick={submitQuiz}>Valider mes réponses</button>
              : quizOk ? <button className="mp-btn-primary" onClick={() => setPhase(cfg.checklist ? 'checklist' : 'conversation')}>{cfg.checklist ? 'Continuer' : 'Parfait, on démarre avec Clara'}</button>
              : <div style={{ display: 'flex', gap: 10 }}>
                  <button className="mp-btn-secondary" onClick={() => { setQuizAnswers({}); setQuizSubmitted(false); setQuizOk(false); }}>Réessayer</button>
                  <button className="mp-btn-primary" onClick={() => setPhase(cfg.checklist ? 'checklist' : 'conversation')}>Continuer quand même →</button>
                </div>}
          </div>
        </div>
      )}

      {phase === 'checklist' && cfg.checklist && (
        <div className="mp-quiz">
          <div className="mp-part-num" style={{ marginBottom: 12 }}>Avant de commencer</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(20px,2.5vw,26px)', color: '#173B34', marginBottom: 16 }}>{cfg.checklist.titre}</h2>
          {cfg.checklist.items.map((it, i) => (
            <div key={i} className={`mp-quiz-opt${checks[i] ? ' selected' : ''}`} style={{ marginBottom: 10 }} onClick={() => setChecks(c => ({ ...c, [i]: !c[i] }))}>{checks[i] ? '✓ ' : '○ '}{it}</div>
          ))}
          {cfg.checklist.note && <div className="mp-highlight" style={{ marginTop: 14 }}>{cfg.checklist.note}</div>}
          <div className="mp-nav-btns"><button className="mp-btn-secondary" onClick={() => setPhase('quiz')}>← Retour</button><button className="mp-btn-primary" onClick={() => setPhase('conversation')}>On démarre avec Clara</button></div>
        </div>
      )}

      {phase === 'conversation' && (
        <div className="mp-conv">
          <div className="mp-step-indicator">
            <div className="mp-step-dots">{Q.map((_, i) => <div key={i} className={`mp-step-dot ${i < convStep ? 'done' : i === convStep ? 'active' : ''}`} />)}</div>
            Question {Math.min(convStep + 1, Q.length)} / {Q.length} · {bloc}
          </div>
          <div className="mp-messages">
            {convMessages.map((msg, i) => (
              <div key={i} className={`mp-msg ${msg.role === 'assistant' ? 'clara' : 'user'}`}>
                <div className="mp-msg-avatar">{msg.role === 'assistant' ? 'C' : '✎'}</div>
                <div className="mp-msg-bubble" style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
              </div>
            ))}
            {convLoading && <div className="mp-msg clara"><div className="mp-msg-avatar">C</div><div className="mp-msg-bubble" style={{ color: '#AAA' }}>Clara réfléchit...</div></div>}
            {error && <div style={{ background: '#FDECEA', border: '1px solid #F5C2BB', color: '#9B2C1F', borderRadius: 12, padding: '10px 14px', fontSize: 14, margin: '8px 0' }}>{error} Réessaie en renvoyant ton message.</div>}
            {!convLoading && Q[convStep]?.quick && convMessages.length > 0 && convMessages[convMessages.length - 1].role === 'assistant' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '6px 0 0 42px' }}>
                {Q[convStep].quick.map((t, i) => <button key={i} onClick={() => setConvInput(t)} style={{ background: '#EAF1EE', color: '#173B34', border: 0, borderRadius: 999, padding: '9px 14px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', minHeight: 38 }}>{t}</button>)}
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="mp-input-wrap">
            <textarea value={convInput} onChange={e => setConvInput(e.target.value)} onKeyDown={onKey} placeholder="Écris ta réponse à Clara" rows={2} disabled={convLoading} />
            <button className="mp-send-btn" onClick={send} disabled={convLoading || !convInput.trim()}>Envoyer</button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div>
          <Fiche cfg={cfg} synthese={synthese} brand={ctx.brand} />
          <div className="mp-nav-btns" style={{ marginTop: 24 }}>
            <button className="mp-btn-secondary" onClick={() => { setPhase('conversation'); setConvMessages([]); setConvStep(0); setSynthese(''); }}>Recommencer</button>
            {!moduleValide && <button className="mp-btn-primary" onClick={() => onValidate(synthese)}>Valider et continuer →</button>}
          </div>
        </div>
      )}
    </div>
  );
}
